import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useCountryConfig } from '../hooks/useCountryConfig';

export default function Settings() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { config } = useCountryConfig();
  const phonePlaceholder = config?.phone?.placeholder ?? '+216 XX XXX XXX';

  const [profile, setProfile] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  const [passwords, setPasswords] = useState({ old_password: '', new_password: '', confirm: '' });
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        first_name: user.first_name || user.prenom || '',
        last_name: user.last_name || user.nom || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    if (!profile.first_name.trim() || !profile.last_name.trim() || !profile.email.trim()) {
      toast.error(t('common.error'), { description: t('accountSettings.toasts.requiredFields') });
      return;
    }
    setSavingProfile(true);
    try {
      await api.put('v1/users/me/', {
        first_name: profile.first_name.trim(),
        last_name: profile.last_name.trim(),
        email: profile.email.trim(),
        phone: profile.phone.trim() || null,
      });
      toast.success(t('accountSettings.toasts.profileUpdated'), { description: t('accountSettings.toasts.profileUpdatedDesc') });
    } catch (err) {
      toast.error(t('common.error'), { description: err?.response?.data?.detail || t('common.operationError') });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwords.old_password || !passwords.new_password || !passwords.confirm) {
      toast.error(t('common.error'), { description: t('accountSettings.toasts.allFieldsRequired') });
      return;
    }
    if (passwords.new_password !== passwords.confirm) {
      toast.error(t('common.error'), { description: t('accountSettings.toasts.passwordMismatch') });
      return;
    }
    setSavingPwd(true);
    try {
      await api.post('v1/users/me/change-password/', {
        old_password: passwords.old_password,
        new_password: passwords.new_password,
      });
      toast.success(t('accountSettings.toasts.passwordChanged'), { description: t('accountSettings.toasts.passwordChangedDesc') });
      setPasswords({ old_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(t('common.error'), { description: err?.response?.data?.detail || t('common.operationError') });
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('accountSettings.title')}</h1>
        <p className="text-muted-foreground">{t('accountSettings.subtitle')}</p>
      </div>

      {/* Profile section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t('accountSettings.profileSection')}
          </CardTitle>
          <CardDescription>{t('accountSettings.profileDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>{t('accountSettings.firstName')} *</Label>
              <Input
                value={profile.first_name}
                onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))}
                placeholder={t('accountSettings.firstName')}
              />
            </div>
            <div className="space-y-1">
              <Label>{t('accountSettings.lastName')} *</Label>
              <Input
                value={profile.last_name}
                onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))}
                placeholder={t('accountSettings.lastName')}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>{t('accountSettings.email')} *</Label>
            <Input
              value={profile.email}
              onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
              placeholder={t('accountSettings.emailPlaceholder')}
              type="email"
            />
          </div>
          <div className="space-y-1">
            <Label>{t('accountSettings.phone')}</Label>
            <Input
              value={profile.phone}
              onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
              placeholder={phonePlaceholder}
            />
          </div>
          <div className="pt-2">
            <Button onClick={handleSaveProfile} disabled={savingProfile}>
              {savingProfile ? t('accountSettings.saving') : t('accountSettings.save')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Password section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            {t('accountSettings.securitySection')}
          </CardTitle>
          <CardDescription>{t('accountSettings.securityDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label>{t('accountSettings.currentPassword')}</Label>
            <div className="relative">
              <Input
                value={passwords.old_password}
                onChange={(e) => setPasswords((p) => ({ ...p, old_password: e.target.value }))}
                type={showOld ? 'text' : 'password'}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                onClick={() => setShowOld((v) => !v)}
              >
                {showOld ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <Label>{t('accountSettings.newPassword')}</Label>
            <div className="relative">
              <Input
                value={passwords.new_password}
                onChange={(e) => setPasswords((p) => ({ ...p, new_password: e.target.value }))}
                type={showNew ? 'text' : 'password'}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                onClick={() => setShowNew((v) => !v)}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <Label>{t('accountSettings.confirmPassword')}</Label>
            <Input
              value={passwords.confirm}
              onChange={(e) => setPasswords((p) => ({ ...p, confirm: e.target.value }))}
              type="password"
              placeholder="••••••••"
              onKeyDown={(e) => e.key === 'Enter' && handleChangePassword()}
            />
          </div>
          <div className="pt-2">
            <Button onClick={handleChangePassword} disabled={savingPwd}>
              {savingPwd ? t('accountSettings.changingPassword') : t('accountSettings.changePassword')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
